import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Select } from '@resurs/midas-react'
import StatisticsStatusBoard from '../../StatisticsStatusBoard'
import StatisticsDistributed from '../../StatisticsDistributed'
import StatisticsTopList from '../../StatisticsTopList'
import { getText } from '../../../../internal/TextLabels'
import styles from './StatisticsListUI.module.scss'
import PeriodSelector from './PeriodSelector'
import { ChainTypes } from '../../../../models/chainTypes'
import { StatisticsScopes } from '../../../../models/constants'
import { sendStatisticFilterEvent } from '../../../../util/ga-helpers'
import { StatisticFilters } from '../statisticFilters'
import DateRangePicker from '../../../../components/common/DateRangePicker/DateRangePicker'
import FilterModule from '../../../../components/common/FilterModule/FilterModule'

const getDateSelection = () => [
  {
    key: 'YEAR',
    value: 'YEAR',
    valueLabel: getText('statistics.selectTimespan.year'),
  },
  {
    key: 'MONTH',
    value: 'MONTH',
    valueLabel: getText('statistics.selectTimespan.month'),
  },
  {
    key: 'WEEK',
    value: 'WEEK',
    valueLabel: getText('statistics.selectTimespan.week'),
  },
]

const getScopeSelection = () => [
  {
    key: StatisticsScopes.STORE,
    value: StatisticsScopes.STORE,
    valueLabel: getText('statistics.selectScope.store'),
  },
  {
    key: StatisticsScopes.REPRESENTATIVE,
    value: StatisticsScopes.REPRESENTATIVE,
    valueLabel: getText('statistics.selectScope.representative'),
  },
]

const getGraphLabels = data => {
  const chartLabels = []
  if (data.length > 0) {
    Object.keys(data[0]).forEach(p => {
      if (p !== 'name') {
        chartLabels.push(p)
      }
    })
  }

  return chartLabels
}

const StatisticsListUI = props => {
  const dateSelection = getDateSelection()
  const scopeSelection = getScopeSelection()

  const labels = useMemo(
    () => ({
      gender: {
        title: getText('statistics.distributionGender.title'),
      },
      channels: {
        title: getText('statistics.distributionChannel.title'),
      },
      returning: {
        title: getText('statistics.distributionReturning.title'),
        graphLabels: [
          getText('statistics.distributionReturning.labels.new'),
          getText('statistics.distributionReturning.labels.returning'),
        ],
      },
      paymentmethods: {
        title: getText('statistics.distributionPaymentMethod.title'),
      },
    }),
    [props.language],
  )

  const renderGraphs = () => {
    const graphTypes = props.privilegedUser
      ? ['gender', 'channels', 'returning', 'paymentmethods']
      : ['paymentmethods']
    return graphTypes
      .filter(graph => !!props.data[graph].bar.length)
      .map(graph => (
        <StatisticsDistributed
          key={graph}
          data={props.data[graph]}
          period={props.period}
          title={labels[graph].title}
          labels={getGraphLabels(props.data[graph].bar)}
          periodType={props.periodType}
          renderText={val => getText('default.currency', val)}
        />
      ))
  }

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <h3 id="title">{getText('statistics.title')}</h3>
        <div className={styles.settings}>
          {props.privilegedUser &&
            props.chainType === ChainTypes.SINGLE_OWNER && (
              <div className={styles.scopeSelector}>
                <Select
                  id="scope"
                  size="large"
                  label={getText('statistics.scope')}
                  value={props.scope}
                  values={scopeSelection}
                  onChange={props.setScope}
                />
              </div>
            )}
          <div className={styles.periodSelector}>
            <Select
              id="period"
              size="large"
              label={getText('statistics.timespan')}
              value={props.periodType}
              values={dateSelection}
              onChange={value => props.setPeriod(value)}
            />
          </div>
          <div className={styles.dateSelector}>
            <PeriodSelector
              selectedPeriod={props.periodType}
              selectedDate={props.selectedDate}
              selectDate={props.selectDate}
              language={props.language}
            />
          </div>
        </div>
        <DateRangePicker
          startDate={props.startDate}
          endDate={props.endDate}
          selectDate={props.selectDate}
          customDate={props.periodType !== 'YEAR'}
        />
        <FilterModule
          filters={props.filters}
          setFilters={props.setFilters}
          values={StatisticFilters}
          onGAEvent={label => {
            sendStatisticFilterEvent(label)
          }}
          onRemoveFilter={props.onRemoveFilter}
        />
        {(props.data.status && (
          <StatisticsStatusBoard
            status={props.data.status}
            timespan={props.timespan}
            scope={props.scope}
            customStart={props.customStart}
          />
        )) ||
          null}
        {(props.data.toplist.list?.length && (
          <StatisticsTopList
            data={props.data.toplist}
            representativeName={props.auth.representative.representativeName}
            storeName={props.auth.store.storeName}
            period={props.period}
            showTrend={userId => props.setTrend(userId)}
          />
        )) ||
          null}
        {renderGraphs()}
        <div style={{ height: '10rem' }} />
      </div>
    </div>
  )
}

StatisticsListUI.propTypes = {
  data: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  period: PropTypes.string.isRequired,
  timespan: PropTypes.string.isRequired,
  scope: PropTypes.string.isRequired,
  setScope: PropTypes.func.isRequired,
  setPeriod: PropTypes.func.isRequired,
  setTrend: PropTypes.func.isRequired,
  periodType: PropTypes.string.isRequired,
  selectDate: PropTypes.func.isRequired,
  selectedDate: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  customStart: PropTypes.bool,
  privilegedUser: PropTypes.bool.isRequired,
  filters: PropTypes.array,
  setFilters: PropTypes.func.isRequired,
  chainType: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  onRemoveFilter: PropTypes.func.isRequired,
}

StatisticsListUI.defaultProps = {
  filters: [],
  customStart: false,
}

export default StatisticsListUI
